import type { UserHydraItem } from "@verbleif/lib/src/composables/Api/Type/User/user.interface";
import { englishLanguageName, languageNames } from "@verbleif/lib/src/components/LocaleSelect/locale";
import { SUPPORTED_LOCALES } from "@verbleif/lib/src/translations/locales";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { usePatchMutation } from "@verbleif/shared/src/composables/Mutation/useHydraMutations";
import { usePersonalTransformer } from "@web/features/Settings/Personal/usePersonalTransformer";
import { computed, ref, watch } from "vue";

/** The user's interface language: the options, the current value and saving a change to the account. */
export function useNavbarLocale() {
  const { user, patchUser } = useAuthStore();
  const { outputPartialTransformer } = usePersonalTransformer();
  const patchLocale = usePatchMutation<UserHydraItem>();

  const languageOptions = computed(() => SUPPORTED_LOCALES.map((locale, index) => ({
    id: index,
    value: locale,
    label: languageNames[locale as keyof typeof languageNames] ?? locale,
    englishName: englishLanguageName(locale) ?? "",
  })));

  const locale = ref(user.value?.locale ?? "");

  async function updateLocale(id: string) {
    if (!user.value) {
      return;
    }
    const data = await patchLocale.mutateAsync({
      url: user.value["@id"],
      body: outputPartialTransformer({
        locale: id,
      }),
    });
    patchUser(data);
  }

  watch(() => locale.value, (newLocale) => {
    void updateLocale(newLocale);
  });

  return {
    languageOptions,
    locale,
  };
}
